/**
 * VisitClinicPatientSearchModal
 *
 * 施術入力用受診者検索モーダル (A2 visit_clinic/modal_search.blade.php 相当).
 * Columns: 番号 / 受診者名 / 受診者名カナ / 住所 — official=1 のみ表示.
 *
 * 自動遷移ルール (A2 ロジック準拠):
 *   - 検索結果が 0 件 → モーダル内に "該当する検索結果がありません" を表示 (gridview 領域)
 *   - 結果が 1 件 かつ keyword が数値:
 *       - onSelectPatient あり → 自動で onSelectPatient 呼出 + モーダル閉じる (継続受診者 直接追加用)
 *       - onSelectPatient 無し → 自動で yearmonthSearch → editInit へ遷移
 *   - それ以外 → モーダルを開いてリスト表示、行クリックで遷移
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/shared/providers/AuthContext';
import { useInfinitePatientSearch } from '@/hooks/main/usePatientSearch';
import { useVisitClinicYearmonthSearch } from '@/hooks/main/useVisitClinic';
import { AppModal, AppSpin, AppTable } from '@/components/atoms';
import { isoMonthToJaWareki } from '@/utils/dateConverter';
import { EMPTY_TEXT_FILTERED } from '@/constants/messages';

export interface PatientRow {
  patient_id: number;
  serial_number: string;
  name: string;
  name_kana: string;
  /** 住所1 */
  address1: string;
  /** 住所2 */
  address2: string;
  /** 建物・部屋 */
  address3: string;
  /** 名称 */
  address_name: string;
}

/** 住所 display: 住所1 + 住所2 + 建物・部屋 + 名称 */
function formatFullAddress(r: Pick<PatientRow, 'address1' | 'address2' | 'address3' | 'address_name'>): string {
  return [r.address1, r.address2, r.address3, r.address_name].filter(Boolean).join(' ');
}

interface VisitClinicPatientSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  keyword: string;
  /** When provided, row click calls this instead of navigating to visit_clinic/edit */
  onSelectPatient?: (patient: PatientRow) => void;
}


const VisitClinicPatientSearchModal: React.FC<VisitClinicPatientSearchModalProps> = ({
  isOpen,
  onClose,
  keyword,
  onSelectPatient,
}) => {
  const { clinic } = useAuth();
  const navigate = useNavigate();

  const siteType = clinic?.site_type ?? '';
  const clinicNumber = clinic?.clinic_number ?? '';
  const { tenant } = useParams<{ tenant: string }>();

  const [hasAutoNavigated, setHasAutoNavigated] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const filters = useMemo(
    () =>
      isOpen
        ? {
            keyword,
            includeExcluded: false,
            sortField: 'name_kana',
            sortOrder: 'asc' as const,
            officialOnly: true,
          }
        : null,
    [isOpen, keyword]
  );

  const {
    data: infiniteData,
    isPending,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    isError,
  } = useInfinitePatientSearch(siteType, clinicNumber, filters, { enabled: isOpen });

  const fetchNextPageRef = useRef(fetchNextPage);
  fetchNextPageRef.current = fetchNextPage;
  const infiniteScrollGuardRef = useRef({ hasNextPage, isFetchingNextPage });
  infiniteScrollGuardRef.current = { hasNextPage, isFetchingNextPage };

  const yearmonthSearch = useVisitClinicYearmonthSearch();

  const patients: PatientRow[] = useMemo(() => {
    if (!infiniteData?.pages?.length) return [];
    return infiniteData.pages.flatMap(page =>
      (page.data ?? []).map(p => ({
        patient_id: p.patient?.patient_id ?? p.id ?? 0,
        serial_number: p.patient?.serial_number ?? p.serial_number?.toString() ?? '',
        name: p.patient_information?.name ?? p.current_patient_information?.name ?? '',
        name_kana:
          p.patient_information?.name_kana ?? p.current_patient_information?.name_kana ?? '',
        address1:
          p.patient_information?.address1 ?? p.current_patient_information?.address1 ?? '',
        address2:
          p.patient_information?.address2 ?? p.current_patient_information?.address2 ?? '',
        address3:
          p.patient_information?.address3 ?? p.current_patient_information?.address3 ?? '',
        address_name:
          p.default_address_name ??
          p.patient_information?.address_name ??
          p.current_patient_information?.address_name ??
          '',
      }))
    );
  }, [infiniteData]);

  const totalCount = useMemo(() => {
    if (!infiniteData?.pages?.length) return 0;
    const lastPage = infiniteData.pages[infiniteData.pages.length - 1];
    return lastPage.pagination?.total ?? patients.length;
  }, [infiniteData, patients.length]);

  // Reset auto-navigate flag when modal closes
  useEffect(() => {
    if (!isOpen) setHasAutoNavigated(false);
  }, [isOpen]);

  // A2 auto-navigate: 1 件 かつ keyword が数値 → 自動遷移 (onSelectPatient が無い場合のみ)
  useEffect(() => {
    if (!isOpen || isPending || hasAutoNavigated) return;
    if (infiniteData === undefined) return;

    const isNumericKeyword = /^\d+$/.test(keyword.trim());

    if (patients.length === 1 && isNumericKeyword) {
      setHasAutoNavigated(true);
      if (onSelectPatient) {
        onSelectPatient(patients[0]);
        onClose();
      } else {
        navigateToEditInit(patients[0].patient_id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isPending, patients, infiniteData]);

  const fetchNextPageWhenNearBottom = useCallback((scrollElement: HTMLElement) => {
    const distanceFromBottom =
      scrollElement.scrollHeight - scrollElement.scrollTop - scrollElement.clientHeight;
    if (distanceFromBottom > 120) return;

    const { hasNextPage: next, isFetchingNextPage: fetching } = infiniteScrollGuardRef.current;
    if (next && !fetching) {
      fetchNextPageRef.current();
    }
  }, []);

  // Infinite scroll: listen to the actual Ant Design table body, not the modal container.
  useEffect(() => {
    if (!isOpen) return;

    const tableBody = tableWrapperRef.current?.querySelector<HTMLElement>('.ant-table-body');
    if (!tableBody) return;

    const handleScroll = () => fetchNextPageWhenNearBottom(tableBody);
    tableBody.addEventListener('scroll', handleScroll);
    return () => tableBody.removeEventListener('scroll', handleScroll);
  }, [isOpen, fetchNextPageWhenNearBottom, infiniteData?.pages?.length]);

  const navigateToEditInit = (patientId: number) => {
    const navigateToEdit = (ym: string) => {
      const jaYearmonth = isoMonthToJaWareki(ym);
      navigate(`/${tenant}/visit_clinic/edit/${patientId}/${jaYearmonth}`);
      onClose();
    };

    yearmonthSearch.mutate(patientId, {
      onSuccess: ({ yearmonth }) => {
        // Convert YYYY-MM from API to Japanese era format for URL (matches A2 behavior)
        navigateToEdit(yearmonth);
      },
      onError: () => {
        navigateToEdit(dayjs().format('YYYY-MM'));
      },
    });
  };

  const columns = [
    {
      title: '番号',
      dataIndex: 'serial_number',
      key: 'serial_number',
      width: 80,
      sorter: (a: PatientRow, b: PatientRow) => Number(a.serial_number) - Number(b.serial_number),
    },
    {
      title: '受診者名',
      dataIndex: 'name',
      key: 'name',
      width: '25%',
      ellipsis: true,
      sorter: (a: PatientRow, b: PatientRow) => (a.name ?? '').localeCompare(b.name ?? ''),
    },
    {
      title: '受診者名カナ',
      dataIndex: 'name_kana',
      key: 'name_kana',
      width: '25%',
      ellipsis: true,
      sorter: (a: PatientRow, b: PatientRow) => (a.name_kana ?? '').localeCompare(b.name_kana ?? ''),
    },
    {
      title: '住所',
      key: 'address',
      ellipsis: true,
      render: (_: unknown, record: PatientRow) => formatFullAddress(record),
      sorter: (a: PatientRow, b: PatientRow) =>
        formatFullAddress(a).localeCompare(formatFullAddress(b)),
    },
  ];

  if (!isOpen) return null;

  const showInitialLoading = isPending && patients.length === 0;
  const showEmpty = !isPending && !isFetchingNextPage && patients.length === 0 && !isError;

  return (
    <AppModal isOpen={isOpen} onClose={onClose} maxWidth="max-w-5xl" title="受診者検索">
      <div className="flex flex-col max-h-[60vh]">
        <div className="flex flex-col rounded mt-4 mb-0 relative">
          {isError ? (
            <div className="py-5 text-center text-red-600">
              検索に失敗しました。再度お試しください。
            </div>
          ) : showEmpty ? (
            <div className="py-5 text-center text-gray-500">{EMPTY_TEXT_FILTERED}</div>
          ) : (
            <>
              <AppSpin spinning={showInitialLoading || yearmonthSearch.isPending} size="large">
                <AppTable
                  ref={tableWrapperRef}
                  columns={columns}
                  dataSource={patients.map(p => ({ ...p, key: p.patient_id }))}
                  pagination={false}
                  scroll={{ y: 400 }}
                  onRow={record => ({
                    onClick: () => {
                      const patient = record as PatientRow;
                      if (onSelectPatient) {
                        onSelectPatient(patient);
                        onClose();
                      } else {
                        navigateToEditInit(patient.patient_id);
                      }
                    },
                    style: { cursor: 'pointer', height: 50 },
                  })}
                />
              </AppSpin>

              {patients.length > 0 && totalCount > 0 && (
                <div className="px-2 text-sm text-gray-600">{totalCount}件</div>
              )}
            </>
          )}
        </div>
      </div>
    </AppModal>
  );
};

export default VisitClinicPatientSearchModal;
